import {
    ALL_EMAIL_FREQUENCY_OF_REMINDERS,
    EMAIL_FREQUENCY_OF_REMINDER_LABELS,
} from '@common/enums';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
    Stack,
    type SxProps,
    TextField,
    type Theme,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect } from 'react';
import LexoSwitch from '@/components/LexoSwitch';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useChangedFlag } from '@/hooks/util';
import { REMINDER_TIME_OPTIONS } from '@/pages/QuoteIntake/constants';
import { useUsers } from '@/queries/users';
import useQuoteIntakeSetupStore from '@/stores/useQuoteIntakeSetupStore';
import { convertToHtml } from '@/utils';
import { infoSnackbar } from '@/utils/snackbar';
import DynamicQuoteStructure from './DynamicQuoteStructure';

const formItemContainerStyle: SxProps<Theme> = {
    display: 'flex',
    position: 'relative',
};

export default function QuoteEmailReminderTemplate() {
    const theme = useTheme();

    const quoteEmailSectionsStyle: SxProps<Theme> = {
        borderRadius: '8px',
        border: `1px solid ${theme.palette.neutral.borderColor}`,
        backgroundColor: 'neutral.white',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        height: 'fit-content',
        width: '100%',
    };

    const [formData, errorData, setFormData, setErrorData, activeQuote] = useQuoteIntakeSetupStore(
        (state) => [
            state.formData,
            state.errorData,
            state.setFormData,
            state.setErrorData,
            state.activeQuote,
        ],
    );
    const { activeLawFirmId } = useLawFirmContext();
    const lawFirmId = activeQuote.lawFirmId ?? activeLawFirmId;

    const hasReminderSubjectChanged = useChangedFlag(formData.step2.reminderSubject);
    const hasReminderBodyBeforePricingChanged = useChangedFlag(
        formData.step2.reminderBodyBeforePricing,
    );
    const hasReminderBodyAfterPricingChanged = useChangedFlag(
        formData.step2.reminderBodyAfterPricing,
    );
    const hasReminderSignatureChanged = useChangedFlag(formData.step2.reminderSignatureOfEmail);

    const [setCcContacts] = useQuoteIntakeSetupStore((state) => [state.setCcContact]);
    const { data: users = [] } = useUsers({ lawFirmId });

    useEffect(() => {
        const filteredCcContacts = users
            .filter((user) => user.userId !== formData.step0.assignedLawyer)
            .map((user) => user.email);
        setCcContacts(filteredCcContacts);
    }, [formData.step0.assignedLawyer, setCcContacts, users]);

    const handleStateChange = (event: any, key: string) => {
        setErrorData({
            ...errorData,
            step2: {
                quoteEmailTemplateError: {
                    emailSubject: undefined,
                    emailSignature: undefined,
                    emailBodyClosing: undefined,
                    emailBodyIntroduction: undefined,
                },
                quoteReminderTemplateErrors: {
                    emailSubject: undefined,
                    emailSignature: undefined,
                    emailBodyClosing: undefined,
                    emailBodyIntroduction: undefined,
                },
            },
        });
        setFormData('step2', {
            ...formData.step2,
            [key]: event.target.value,
        });
    };

    const errorkey = 'quouteRemindertemplateError';

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '24px',
                maxHeight: 'calc(100vh - 168px)',
                overflow: 'auto',
            }}
            p={4}
        >
            <Box sx={quoteEmailSectionsStyle}>
                <Stack spacing={1}>
                    <Typography variant="h2">Quote email reminder template</Typography>
                    <Typography variant="body2">
                        Customize your email template and see changes on preview on the right.
                    </Typography>
                </Stack>
                <Divider orientation="horizontal" />
                <Stack spacing={2}>
                    <Typography variant="body2">Enable quote reminders</Typography>
                    <LexoSwitch
                        checked={formData.step2.isQuoteReminderOn}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const ev = { target: { value: event.target.checked } };
                            if (formData.step2.isQuoteReminderOn) {
                                infoSnackbar('Reminders are currently set to off');
                            }

                            handleStateChange(ev, 'isQuoteReminderOn');
                        }}
                    />
                    <Typography variant="body3">
                        Enable quote reminders to set the amount and frequency of reminders sent to
                        your clients in case they don’t respond.
                    </Typography>
                    <Accordion expanded={formData.step2.isQuoteReminderOn}>
                        <AccordionSummary>
                            <Typography
                                variant={'labelLarge'}
                                color={
                                    formData.step2.isQuoteReminderOn
                                        ? theme.palette.neutral.black
                                        : theme.palette.neutral.gray
                                }
                            >
                                Reminders frequency
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                '&.MuiAccordionDetails-root': {
                                    paddingTop: '0px',
                                },
                            }}
                        >
                            <Stack spacing={2}>
                                <Divider orientation="horizontal" />
                                <FormControl fullWidth>
                                    <InputLabel id="reminder-count-label">
                                        Total number of quote reminders
                                    </InputLabel>
                                    <Select
                                        labelId="reminder-count-label"
                                        label="Total number of quote reminders"
                                        disabled={!formData.step2.isQuoteReminderOn}
                                        value={formData.step2.totalNumberOfReminders}
                                        onChange={(event) =>
                                            handleStateChange(event, 'totalNumberOfReminders')
                                        }
                                    >
                                        <MenuItem value={1}>1</MenuItem>
                                        <MenuItem value={2}>2</MenuItem>
                                        <MenuItem value={3}>3</MenuItem>
                                        <MenuItem value={4}>4</MenuItem>
                                        <MenuItem value={5}>5</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth>
                                    <InputLabel id="reminder-frequency-label">
                                        Frequency of quote reminders
                                    </InputLabel>
                                    <Select
                                        labelId="reminder-frequency-label"
                                        disabled={!formData.step2.isQuoteReminderOn}
                                        label="Frequency of quote reminders"
                                        value={formData.step2.emailFrequencyOfReminder}
                                        onChange={(event: SelectChangeEvent<unknown>) =>
                                            handleStateChange(event, 'emailFrequencyOfReminder')
                                        }
                                    >
                                        {ALL_EMAIL_FREQUENCY_OF_REMINDERS.map((frequency) => (
                                            <MenuItem key={frequency} value={frequency}>
                                                {EMAIL_FREQUENCY_OF_REMINDER_LABELS[frequency]}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth>
                                    <InputLabel id="reminder-time-label">Reminders time</InputLabel>
                                    <Select
                                        labelId="reminder-time-label"
                                        label="Reminders time"
                                        disabled={!formData.step2.isQuoteReminderOn}
                                        value={formData.step2.reminderTime}
                                        onChange={(event: SelectChangeEvent<unknown>) =>
                                            handleStateChange(event, 'reminderTime')
                                        }
                                    >
                                        {Object.entries(REMINDER_TIME_OPTIONS).map(
                                            ([key, value]) => (
                                                <MenuItem key={key} value={value}>
                                                    {key}
                                                </MenuItem>
                                            ),
                                        )}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                </Stack>
                <Divider orientation="horizontal" />
                <Stack spacing={2}>
                    <Typography variant="body2">
                        Notify via email when the maximum number of reminders has been sent to the
                        client.
                    </Typography>
                    <LexoSwitch
                        checked={formData.step2.enableReminderExhaustNotification}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const ev = { target: { value: event.target.checked } };
                            handleStateChange(ev, 'enableReminderExhaustNotification');
                        }}
                    />
                </Stack>
                <Divider orientation="horizontal" />
                <Stack spacing={4}>
                    <Box sx={formItemContainerStyle}>
                        <TextField
                            value={formData.step2.reminderSubject}
                            onChange={(ev) => handleStateChange(ev, 'reminderSubject')}
                            name="reminderSubject"
                            error={!!errorData.step2[errorkey]?.emailSubject}
                            helperText={errorData.step2[errorkey]?.emailSubject}
                            fullWidth
                            placeholder="e.g. Quote Email Reminder"
                            type="text"
                            variant="outlined"
                            label="Subject"
                        />
                    </Box>
                    <Box sx={formItemContainerStyle}>
                        <TextField
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: 'initial',
                                },
                            }}
                            value={formData.step2.reminderBodyBeforePricing}
                            onChange={(ev) => handleStateChange(ev, 'reminderBodyBeforePricing')}
                            name="reminderBodyBeforePricing"
                            error={!!errorData.step2[errorkey]?.emailBodyIntroduction}
                            helperText={
                                errorData.step2[errorkey]?.emailBodyIntroduction
                                    ? 'Email body introduction is required.'
                                    : ''
                            }
                            fullWidth
                            placeholder=""
                            type="text"
                            multiline={true}
                            rows={3}
                            variant="outlined"
                            label="First part of body (before fee)"
                        />
                    </Box>
                    <Box sx={formItemContainerStyle}>
                        <TextField
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: 'initial',
                                },
                            }}
                            value={formData.step2.reminderBodyAfterPricing}
                            onChange={(ev) => handleStateChange(ev, 'reminderBodyAfterPricing')}
                            name="reminderBodyAfterPricing"
                            error={!!errorData.step2[errorkey]?.emailBodyClosing}
                            helperText={
                                errorData.step2[errorkey]?.emailBodyClosing
                                    ? 'Email body closing is required.'
                                    : ''
                            }
                            fullWidth
                            placeholder=""
                            type="text"
                            multiline={true}
                            rows={3}
                            variant="outlined"
                            label="Second part of body (after fee)"
                        />
                    </Box>
                    <Box sx={formItemContainerStyle}>
                        <TextField
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: 'initial',
                                },
                            }}
                            value={formData.step2.reminderDisclaimerText}
                            onChange={(ev) => handleStateChange(ev, 'reminderDisclaimerText')}
                            name="reminderDisclaimerText"
                            fullWidth
                            placeholder=""
                            type="text"
                            multiline={true}
                            rows={3}
                            variant="outlined"
                            label="Disclaimer text"
                        />
                    </Box>
                </Stack>
            </Box>

            <Box sx={quoteEmailSectionsStyle}>
                <Stack spacing={1}>
                    <Typography variant="h2">Template preview</Typography>
                    <Typography variant="body2">
                        This is a preview of the email that your clients will see in their inbox.
                    </Typography>
                </Stack>
                <Divider orientation="horizontal" />
                <Box
                    sx={{
                        borderRadius: '4px',
                        border: '1px solid #E0E3E5',
                        background: '#F0F5FD',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        padding: '16px 0',
                        '& > *': {
                            padding: '16px',
                        },
                        wordWrap: 'break-word',
                        overflowWrap: 'anywhere',
                    }}
                >
                    <Typography
                        variant="h3"
                        className={hasReminderSubjectChanged ? 'activity-tracker' : ''}
                    >
                        {formData.step2.reminderSubject || 'Quote Email Reminder'}
                    </Typography>
                    <Typography variant="body1" sx={{ marginTop: '8px' }}>
                        Hi [client name],
                    </Typography>
                    <Typography
                        variant="body1"
                        className={hasReminderBodyBeforePricingChanged ? 'activity-tracker' : ''}
                    >
                        {formData.step2.reminderBodyBeforePricing && (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: convertToHtml(formData.step2.reminderBodyBeforePricing),
                                }}
                            ></span>
                        )}
                    </Typography>
                    <DynamicQuoteStructure />
                    <Typography
                        variant="body1"
                        className={hasReminderBodyAfterPricingChanged ? 'activity-tracker' : ''}
                    >
                        {formData.step2.reminderBodyAfterPricing && (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: convertToHtml(formData.step2.reminderBodyAfterPricing),
                                }}
                            ></span>
                        )}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <Typography variant="h3" color="#16171A">
                            Select one of the options below to respond
                        </Typography>
                        <Box sx={{ display: 'flex', gap: '8px' }}>
                            <Button
                                variant="contained"
                                disabled={true}
                                sx={{ textTransform: 'none' }}
                            >
                                Accept
                            </Button>
                            <Button
                                variant="outlined"
                                disabled={true}
                                sx={{ textTransform: 'none' }}
                            >
                                Request a call
                            </Button>
                            <Button
                                variant="outlined"
                                disabled={true}
                                sx={{ textTransform: 'none' }}
                            >
                                Decline
                            </Button>
                        </Box>
                    </Box>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '12px',
                            color: '#555',
                            fontStyle: 'italic',
                            lineHeight: 1.5,
                            mt: 1.5,
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {formData.step2.reminderDisclaimerText && (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: convertToHtml(formData.step2.reminderDisclaimerText),
                                }}
                            />
                        )}
                    </Typography>
                    <Typography
                        variant="body1"
                        className={hasReminderSignatureChanged ? 'activity-tracker' : ''}
                    >
                        {formData.step2.reminderSignatureOfEmail && (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: convertToHtml(formData.step2.reminderSignatureOfEmail),
                                }}
                            ></span>
                        )}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
